import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Settings2,
  LayoutGrid,
  RotateCcw,
  Briefcase,
  PieChart,
  BarChart3,
  Calendar,
  Users,
  ThumbsUp,
  AlertTriangle,
  TrendingUp,
  FileText,
  CheckCircle2,
  Grip,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";

export interface WidgetConfig {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: "stats" | "charts" | "lists" | "summaries";
  enabled: boolean;
  size: "small" | "medium" | "large";
}

interface DashboardCustomizerProps {
  widgets: WidgetConfig[];
  onWidgetsChange: (widgets: WidgetConfig[]) => void;
  onResetToDefault: () => void;
}

const categoryLabels: Record<string, string> = {
  stats: "Statistics",
  charts: "Charts & Graphs",
  lists: "Lists & Tables",
  summaries: "Summaries",
};

const categoryIcons: Record<string, React.ReactNode> = {
  stats: <TrendingUp size={16} />,
  charts: <BarChart3 size={16} />,
  lists: <FileText size={16} />,
  summaries: <CheckCircle2 size={16} />,
};

const DashboardCustomizer = ({
  widgets,
  onWidgetsChange,
  onResetToDefault,
}: DashboardCustomizerProps) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [open, setOpen] = useState(false);

  const toggleWidget = (widgetId: string) => {
    const updated = widgets.map((w) =>
      w.id === widgetId ? { ...w, enabled: !w.enabled } : w
    );
    onWidgetsChange(updated);
  };

  const enabledCount = widgets.filter((w) => w.enabled).length;
  const categories = [...new Set(widgets.map((w) => w.category))];

  const handleEnableAll = () => {
    onWidgetsChange(widgets.map((w) => ({ ...w, enabled: true })));
  };

  const handleDisableAll = () => {
    onWidgetsChange(widgets.map((w) => ({ ...w, enabled: false })));
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings2 size={16} />
          <span className="hidden sm:inline">{t("customize") || "Customize"}</span>
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <LayoutGrid size={20} />
            {t("customizeDashboard") || "Customize Dashboard"}
          </SheetTitle>
          <SheetDescription>
            {t("customizeDashboardDesc") || "Choose which widgets to display on your dashboard"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Quick Actions */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="gap-1">
                {enabledCount}/{widgets.length} active
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleEnableAll}>
                {t("enableAll") || "Enable All"}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDisableAll}>
                {t("disableAll") || "Disable All"}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Widget List by Category */}
          <ScrollArea className="h-[calc(100vh-320px)]">
            <div className="space-y-6 pr-4">
              {categories.map((category) => (
                <div key={category} className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                    {categoryIcons[category]}
                    {categoryLabels[category]}
                  </div>
                  <div className="space-y-2">
                    {widgets
                      .filter((w) => w.category === category)
                      .map((widget) => (
                        <div
                          key={widget.id}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            widget.enabled
                              ? "bg-primary/5 border-primary/20"
                              : "bg-muted/30 border-transparent"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`p-2 rounded-lg ${
                                widget.enabled
                                  ? "bg-primary/10 text-primary"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {widget.icon}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{widget.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {widget.description}
                              </p>
                            </div>
                          </div>
                          <Switch
                            checked={widget.enabled}
                            onCheckedChange={() => toggleWidget(widget.id)}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          <Separator />

          {/* Reset Button */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onResetToDefault();
                setOpen(false);
              }}
              className="gap-2"
            >
              <RotateCcw size={14} />
              {t("resetToDefault") || "Reset to Default"}
            </Button>
            <Button size="sm" onClick={() => setOpen(false)}>
              {t("customizationDone") || "Done"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DashboardCustomizer;
